import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks: { label: string; to?: string; href?: string }[] = [
  { label: 'Inicio', to: '/' },
  { label: 'Nosotros', to: '/nosotros' },
  { label: 'Servicios', to: '/servicios' },
  { label: 'Compraventa', to: '/compraventa' },
  { label: 'Social', to: '/social' },
  { label: 'Contacto', to: '/contacto' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const bgClass = isHome
    ? scrolled
      ? 'bg-black/95 backdrop-blur-md border-b border-zinc-900'
      : 'bg-transparent'
    : 'bg-black/97 backdrop-blur-md border-b border-zinc-900';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgClass}`}
      style={{ willChange: 'background-color' }}
    >
      <div className="w-full px-6 md:px-10 h-16 md:h-18 flex items-center justify-between relative">
        <Link to="/" className="flex-shrink-0 cursor-pointer group z-10">
          <img
            alt="Taller Kevin Alberto"
            className="h-9 w-auto object-contain transition-opacity duration-300 group-hover:opacity-80"
            src="https://static.readdy.ai/image/ddfb463a64ca8c28bad8258e53257cac/7fa385eb80b3da87383713583b172ef6.png"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
          {navLinks.map((link) => {
            const active = link.to ? location.pathname === link.to : false;
            const sharedClass = `relative text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 cursor-pointer whitespace-nowrap group ${
              active ? 'text-yellow-400' : 'text-white/75 hover:text-white'
            }`;
            const underline = (
              <span
                className={`absolute -bottom-1 left-0 h-px bg-yellow-400 transition-all duration-300 ${
                  active ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
              />
            );
            if (link.href) {
              return (
                <a key={link.label} href={link.href} className={sharedClass}>
                  {link.label}
                  {underline}
                </a>
              );
            }
            return (
              <Link key={link.label} to={link.to!} className={sharedClass}>
                {link.label}
                {underline}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3 z-10">
          <a
            href="tel:+34666365922"
            className="hidden lg:flex items-center gap-2 text-zinc-400 hover:text-yellow-400 text-xs transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line" />
            <span className="font-medium">+34 666 36 59 22</span>
          </a>
          <Link
            to="/contacto"
            className="hidden md:inline-flex items-center gap-2 bg-yellow-400 text-black text-[11px] font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-yellow-300 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Presupuesto <i className="ri-arrow-right-line" />
          </Link>
          <button
            className="md:hidden text-white text-xl cursor-pointer w-8 h-8 flex items-center justify-center"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`transition-all duration-300 ${mobileOpen ? 'ri-close-line text-yellow-400' : 'ri-menu-3-line'}`} />
          </button>
        </div>
      </div>

      <div
        className={`md:hidden overflow-hidden transition-all duration-400 ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-black/98 backdrop-blur-md border-t border-zinc-800 px-6 py-6">
          <nav className="flex flex-col gap-5">
            {navLinks.map((link) => {
              const active = link.to ? location.pathname === link.to : false;
              const sharedClass = `flex items-center justify-between text-sm font-semibold uppercase tracking-widest transition-colors cursor-pointer whitespace-nowrap py-1 border-b border-zinc-900 ${
                active ? 'text-yellow-400 border-yellow-400/20' : 'text-white/80 hover:text-yellow-400'
              }`;
              if (link.href) {
                return (
                  <a key={link.label} href={link.href} className={sharedClass}>
                    {link.label}
                    <i className="ri-arrow-right-s-line text-zinc-600" />
                  </a>
                );
              }
              return (
                <Link key={link.label} to={link.to!} className={sharedClass}>
                  {link.label}
                  <i className="ri-arrow-right-s-line text-zinc-600" />
                </Link>
              );
            })}
          </nav>
          <div className="mt-6 flex flex-col gap-3">
            <a
              href="tel:+34666365922"
              className="flex items-center gap-2 text-zinc-400 text-sm"
            >
              <i className="ri-phone-line text-yellow-400" />
              +34 666 36 59 22
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center gap-2 bg-yellow-400 text-black text-xs font-bold uppercase tracking-widest px-5 py-3 hover:bg-yellow-300 transition-all whitespace-nowrap cursor-pointer w-full"
            >
              Pedir presupuesto gratis <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
